import { buildQueueEntry, type useAudioPlaybackStore } from '@/stores/playerStore';
import {
    fetchTrackListQueue,
    type TrackListQueueEntryPayload,
} from '@/composables/api/useTrackListQueueApi';
import type { TrackQueueContext } from '@/composables/trackQueueContext';
import type { QueueEntry } from '@/types/playerQueue';
import type { PlaylistTrackRow } from '@/composables/api/usePlaylistSwitchingApi';

/**
 * One queue payload as the track row a card renders from.
 *
 * A near-rename, not a transformation: the endpoint sends `PlaylistTrackRow`'s
 * field names on purpose, so that a queued card and the list row it was queued
 * from are the same card. The two genuine differences are `id`, which the queue
 * calls `live_track_id` because an entry's identity is its code, and `tags`,
 * which is optional on the row type and always present here.
 */
function cardFromPayload(payload: TrackListQueueEntryPayload): PlaylistTrackRow | null {
    // A backend from before tabasco#689 sends none of the card fields. That has
    // to read as "no card" — the queue then renders its plain row, which is what
    // it rendered before any of this — rather than as a card whose every field
    // is undefined. `name` stands in for the set: the endpoint sends all of them
    // or none.
    if (payload.name === undefined) {
        return null;
    }

    return {
        id: payload.live_track_id,
        code: payload.code,
        name: payload.name,
        remix_name: payload.remix_name ?? null,
        artist_id: payload.artist_id ?? null,
        artist_name: payload.artist,
        album_id: payload.album_id ?? null,
        album_name: payload.album,
        genre_name: payload.genre_name ?? null,
        rating: payload.rating ?? null,
        playlist_count: payload.playlist_count ?? null,
        isrc: payload.isrc ?? null,
        bitrate: payload.bitrate ?? null,
        duration: payload.duration ?? null,
        copyright: payload.copyright ?? null,
        cover_art_signed_url: payload.cover_art_signed_url,
        uploaded_date: payload.uploaded_date ?? null,
        created_at: payload.created_at ?? null,
        position: payload.position,
        tags: payload.tags ?? [],
        lyrics_check_status: payload.lyrics_check_status ?? null,
    };
}

/**
 * Adopt the list behind `context` as the play queue, seated on `seedEntry`.
 *
 * Two gestures land here and they must behave identically, because they queue
 * the same list: clicking Play on a row (seat on that row) and the list's own
 * "Play all" (seat on its first row). Keeping the fetch, the entry mapping and
 * the one-track short-circuit in one function is what stops them drifting —
 * "Play all" silently skipping the short-circuit, say, or mapping a field the
 * row button doesn't.
 *
 * Returns false only when the list could not be loaded. The seed track is
 * playing either way: {@link playListInContext} starts it before the list is
 * asked for, so a failed list leaves a queue of one rather than silence.
 */
export async function playTrackListInContext(
    playback: ReturnType<typeof useAudioPlaybackStore>,
    seedEntry: QueueEntry,
    context: TrackQueueContext
): Promise<boolean> {
    return playback.playListInContext(seedEntry, async () => {
        // A list of one is already in hand: the seed row IS the whole queue, so
        // there is nothing behind it to fetch. Asking anyway was a round trip
        // that could only ever return the row we are holding — and when it
        // failed it said "Could not load the rest of the list" over a list with
        // no rest. The label still comes from the surface, so the queue view
        // names the list rather than falling back to "Play queue".
        if (context.knownTotal === 1) {
            return {
                entries: [seedEntry],
                total: 1,
                truncated: false,
                label: context.label ?? null,
            };
        }

        const response = await fetchTrackListQueue(context.source);

        return {
            // No has_audio filter: the endpoint's contract is that everything it
            // returns is playable — it drops unplayable rows server-side, which is
            // what `meta.returned < meta.total` reports. The field it used to send
            // could only ever be true.
            entries: response.entries.map((payload) =>
                buildQueueEntry({
                    identity: { kind: 'live_track', code: payload.code },
                    source: 'live_mp3',
                    code: payload.code,
                    title: payload.title,
                    artist: payload.artist,
                    album: payload.album,
                    coverUrl: payload.cover_art_signed_url,
                    position: payload.position,
                    // All three records travel with the entry, so the bar's
                    // identity lines keep working as the queue advances. They
                    // used to die on the first auto-advance: only the clicked
                    // entry had artist and album ids, because the payload sent
                    // those two as names only (fixed in tabasco#688).
                    trackEntityId: 'LiveTrack',
                    trackRecordId: payload.live_track_id,
                    artistId: payload.artist_id ?? null,
                    albumId: payload.album_id ?? null,
                    card: cardFromPayload(payload),
                })
            ),
            total: response.meta.total,
            truncated: response.meta.truncated,
            label: context.label ?? response.meta.label,
        };
    });
}
